/**
 * Detect whether a certificate file is a PDF.
 */
export const isPdfFile = (file) => {
  if (!file) return false;

  if (typeof file === "string") {
    const lower = file.toLowerCase();
    return (
      lower.includes(".pdf") ||
      file.includes("/raw/") ||
      /\/upload\/.*f_pdf/.test(file)
    );
  }

  if (file instanceof File || file instanceof Blob) {
    return file.type === "application/pdf";
  }

  const format = file.format?.toLowerCase();
  const url = file.url || "";
  const lowerUrl = url.toLowerCase();

  return (
    format === "pdf" ||
    file.resource_type === "raw" ||
    lowerUrl.includes(".pdf") ||
    url.includes("/raw/") ||
    /\/upload\/.*f_pdf/.test(url)
  );
};

/**
 * Return a browser-openable Cloudinary URL.
 * Fixes broken PDF URLs missing /image/ or /raw/ before /upload/.
 */
export const getCertificateFileUrl = (file) => {
  if (!file) return null;
  if (typeof file === "string") {
    return normalizeCloudinaryUrl(file, true);
  }

  const url = file.url;
  if (!url) return null;

  return normalizeCloudinaryUrl(url, isPdfFile(file), file.resource_type);
};

const normalizeCloudinaryUrl = (url, treatAsPdf, resourceType) => {
  if (!url) return url;

  let fixed = url;

  // Broken: https://res.cloudinary.com/{cloud}/upload/v...
  // Valid:  https://res.cloudinary.com/{cloud}/image/upload/v...
  if (/res\.cloudinary\.com\/[^/]+\/upload\//.test(fixed)) {
    const type =
      resourceType === "raw"
        ? "raw"
        : treatAsPdf || fixed.toLowerCase().includes(".pdf")
          ? "image"
          : "image";
    fixed = fixed.replace(
      /(res\.cloudinary\.com\/[^/]+)\/upload\//,
      `$1/${type}/upload/`
    );
  }

  // Prefer public image delivery for PDFs (raw often 401 without signed URLs)
  if (
    treatAsPdf &&
    fixed.includes("/raw/upload/") &&
    fixed.toLowerCase().includes(".pdf")
  ) {
    fixed = fixed.replace("/raw/upload/", "/image/upload/");
  }

  // Image PDF without .pdf / f_pdf — force pdf format
  if (
    treatAsPdf &&
    fixed.includes("/image/upload/") &&
    !fixed.toLowerCase().includes(".pdf") &&
    !/\/upload\/.*f_pdf/.test(fixed)
  ) {
    fixed = fixed.replace("/image/upload/", "/image/upload/f_pdf/");
  }

  return fixed;
};
